import React from 'react';
import { useRoughCanvas } from '@/hooks/useRoughCanvas';

interface RoughSliderProps {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  label: string;
  seed?: number;
  roughness?: number;
  trackColor?: string;
  fillColor?: string;
  thumbColor?: string;
  strokeWidth?: number;
  thumbRadius?: number;
  className?: string;
  disabled?: boolean;
}

const RoughSlider: React.FC<RoughSliderProps> = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  label,
  seed = 1,
  roughness = 1.2,
  trackColor = '#d2dae5',
  fillColor = '#428fe9',
  thumbColor = '#428fe9',
  strokeWidth = 2,
  thumbRadius = 11,
  className = '',
  disabled = false,
}) => {
  const canvasRef = useRoughCanvas(
    (rc, w, h) => {
      const padding = thumbRadius + 4;
      const trackY = h / 2;
      const trackStartX = padding;
      const trackEndX = w - padding;
      const trackLength = trackEndX - trackStartX;
      const ratio = Math.max(0, Math.min(1, (value - min) / (max - min)));
      const thumbX = trackStartX + ratio * trackLength;

      rc.line(trackStartX, trackY, trackEndX, trackY, {
        stroke: trackColor, strokeWidth, roughness, seed,
      });

      if (ratio > 0.01) {
        rc.line(trackStartX, trackY, thumbX, trackY, {
          stroke: fillColor, strokeWidth: strokeWidth + 1, roughness: roughness * 0.7, seed: seed + 10,
        });
      }

      rc.circle(thumbX, trackY, thumbRadius * 2, {
        fill: thumbColor, fillStyle: 'solid', stroke: thumbColor, strokeWidth: 1.5,
        roughness: roughness * 1.1, seed: seed + 20,
      });

      rc.circle(thumbX, trackY, thumbRadius * 0.7, {
        fill: '#ffffff', fillStyle: 'solid', stroke: 'transparent', strokeWidth: 0,
        roughness: 0.4, seed: seed + 30,
      });
    },
    [value, min, max, trackColor, fillColor, thumbColor, roughness, strokeWidth, seed, thumbRadius],
  );

  return (
    <div className={`rough-slider ${disabled ? 'rough-slider--disabled' : ''} ${className}`}>
      <canvas
        ref={canvasRef}
        className="rough-slider__canvas"
        aria-hidden="true"
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        disabled={disabled}
        className="rough-slider__input"
      />
    </div>
  );
};

export default RoughSlider;
